import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { useMe } from "@/api/hooks";
import { useQuotes } from "@/api/sales";
import { Badge } from "@/components/crm/Badge";
import { Icon } from "@/components/icons";
import { EmptyState, PageHeader, Spinner } from "@/components/ui";
import { money, shortDate } from "@/lib/format";
import { can } from "@/lib/permissions";
import { QuoteStatusBadge } from "./QuoteStatusBadge";

const TABS = [["", "All"], ["draft", "Drafts"], ["sent", "Sent"], ["accepted", "Accepted"], ["rejected", "Declined"], ["expired", "Expired"]] as const;

export function QuotesPage() {
  const perms = useMe().data?.permissions ?? [];
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(params.get("approval") === "pending" ? "approval" : "");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => { const t = setTimeout(() => setQ(search.trim()), 250); return () => clearTimeout(t); }, [search]);
  const quotes = useQuotes(status === "approval" ? { approval: "pending", q: q || undefined } : { status: status || undefined, q: q || undefined });
  const tabs = can(perms, "quotes:approve") ? [...TABS, ["approval", "Needs approval"] as const] : TABS;
  const newButton = can(perms, "quotes:create") && <Link to="/quotes/new" className="btn-primary"><Icon name="plus" />New quote</Link>;

  return (
    <>
      <PageHeader title="Quotes" subtitle="Quotations taxed for each customer's country, with a PDF and an online accept link." actions={newButton} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input type="search" className="input max-w-xs" placeholder="Search number or subject" aria-label="Search quotes" value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="inline-flex flex-wrap rounded-md border border-line bg-surface p-0.5" role="tablist" aria-label="Quote status">
          {tabs.map(([value, label]) => (
            <button key={value} type="button" role="tab" aria-selected={status === value} onClick={() => setStatus(value)}
              className={`rounded px-3 py-1 text-sm font-medium ${status === value ? "bg-brand text-white" : "text-muted hover:text-ink"}`}>{label}</button>
          ))}
        </div>
      </div>
      {quotes.isLoading && <Spinner />}
      {quotes.data?.items.length === 0 && <EmptyState title="No quotes here" body="Create a quote from a deal, or start one here." action={newButton} />}
      {!!quotes.data?.items.length && (
        <div className="card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-line bg-canvas text-xs uppercase tracking-wide text-muted">
              <tr><th className="px-4 py-3">Quote</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3">Valid until</th><th className="px-4 py-3">Status</th></tr>
            </thead>
            <tbody>
              {quotes.data.items.map((qt) => (
                <tr key={qt.id} className="cursor-pointer border-b border-line last:border-0 hover:bg-canvas" onClick={() => navigate(`/quotes/${qt.id}`)}>
                  <td className="px-4 py-3"><Link to={`/quotes/${qt.id}`} className="font-medium hover:text-brand" onClick={(e) => e.stopPropagation()}>{qt.number}{qt.version > 1 && <span className="text-muted"> rev. {qt.version}</span>}</Link><span className="block text-xs text-muted">{qt.title}</span></td>
                  <td className="px-4 py-3">{qt.account?.name ?? <span className="text-muted">—</span>}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-medium tabular-nums">{money(qt.grand_total, qt.currency)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-muted">{shortDate(qt.valid_until)}</td>
                  <td className="px-4 py-3"><span className="flex flex-wrap gap-1"><QuoteStatusBadge status={qt.display_status} />{qt.status === "draft" && ["required", "pending", "rejected"].includes(qt.approval_status) && <Badge tone="warn">{qt.approval_status === "pending" ? "Awaiting approval" : qt.approval_status === "rejected" ? "Approval declined" : "Needs approval"}</Badge>}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
